<template>
  <button
    type="button"
    role="button"
    class="portal-card"
    :class="{
      'portal-card--disabled': disabled,
      'portal-card--loading': loading
    }"
    :aria-label="title"
    :aria-disabled="disabled"
    :disabled="disabled"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="portal-card__icon-container">
      <div v-if="loading" class="portal-card__spinner" role="status" aria-label="Loading">
        <LucideIcon name="Loader2" :size="48" />
      </div>
      <LucideIcon v-else :name="icon" :size="48" />
    </div>
    <h3 class="portal-card__title">{{ title }}</h3>
    <p v-if="description" class="portal-card__description">{{ description }}</p>
  </button>
</template>

<script lang="ts">
import Vue from 'vue';
import LucideIcon from '../LucideIcon/LucideIcon.vue';

export default Vue.extend({
  name: 'PortalCard',

  components: {
    LucideIcon
  },

  props: {
    id: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'LayoutDashboard'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    handleClick() {
      if (this.disabled || this.loading) {
        return;
      }
      this.$emit('click', this.id);
    }
  }
});
</script>

<style lang="scss" scoped>
.portal-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  background: #ffffff;
  background: var(--card, #ffffff);
  border: 1px solid #e5e7eb;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: var(--radius);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 200ms ease;
  text-align: center;
  min-width: 200px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:hover:not(&--disabled):not(&--loading) {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    border-color: #406A94;
    border-color: var(--primary, #406A94);
  }

  &:focus-visible {
    outline: 2px solid #406A94;
    outline: 2px solid var(--primary, #406A94);
    outline-offset: 2px;
  }

  &:active:not(&--disabled):not(&--loading) {
    transform: translateY(0);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  &--loading {
    cursor: wait;
  }

  &__icon-container {
    margin-bottom: 16px;
    color: #406A94;
    color: var(--primary, #406A94);
  }

  &__spinner {
    animation: spin 1s linear infinite;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    color: #111827;
    color: var(--foreground, #111827);
    margin: 0 0 8px 0;
  }

  &__description {
    font-size: 14px;
    line-height: 1.5;
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
    margin: 0;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
// Dark theme adjustments (unscoped for .dark selector)
.dark .portal-card {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2);

  &:hover:not(.portal-card--disabled):not(.portal-card--loading) {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3);
    border-color: #60a5fa;
    border-color: var(--primary, #60a5fa);
  }

  .portal-card__icon-container {
    color: #60a5fa;
    color: var(--primary, #60a5fa);
  }

  .portal-card__title {
    color: #f9fafb;
  }

  .portal-card__description {
    color: #9ca3af;
  }
}
</style>
